import { get } from 'svelte/store'
import { usageStore, usageActions, type UsageEntry } from '$lib/stores/usageStore'
import { HttpClient } from '$lib/utils/httpClient'
import { getSubscriptionRequestHeaders } from '$lib/services/subscription/requestMetadata'

export interface TrackingEntry {
  type: 'grammar-check' | 'ai-analysis'
  timestamp: number
  metadata: Record<string, any>
}

export interface SyncResult {
  success: boolean
  synced: number
  failed: number
  timestamp: number
}

/**
 * Hybrid usage tracking service
 * - Local caching for offline support
 * - Server sync for centralized tracking
 * - Automatic retry and batching
 */
export class UsageTrackingService {
  private static instance: UsageTrackingService
  private syncQueue: TrackingEntry[] = []
  private syncTimer: NodeJS.Timeout | null = null
  private isOnline = true
  private readonly SYNC_INTERVAL = 5 * 60 * 1000 // 5 minutes
  private readonly BATCH_SIZE = 50
  private readonly PROXY_URL = import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org'
  private rateLimitCache: Map<string, { result: any; timestamp: number }> = new Map()
  private readonly RATE_LIMIT_CACHE_TTL = 60 * 1000 // Cache for 60 seconds to reduce API calls
  private pendingRequests: Map<string, Promise<any>> = new Map() // Track pending requests
  
  private constructor() {
    this.initializeEventListeners()
    this.startSyncTimer()
  }
  
  static getInstance(): UsageTrackingService {
    if (!UsageTrackingService.instance) {
      UsageTrackingService.instance = new UsageTrackingService()
    }
    return UsageTrackingService.instance
  }
  
  private initializeEventListeners() {
    // Monitor online/offline status
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.isOnline = true
        this.syncPendingData()
      })
      
      window.addEventListener('offline', () => {
        this.isOnline = false
      })
      
      // Sync before page unload
      window.addEventListener('beforeunload', () => {
        this.syncPendingData(true) // Force sync
      })
    }
  }
  
  private startSyncTimer() {
    this.syncTimer = setInterval(() => {
      if (this.isOnline && this.syncQueue.length > 0) {
        this.syncPendingData()
      }
    }, this.SYNC_INTERVAL)
  }
  
  /**
   * Track grammar check usage
   */
  async trackGrammarCheck(metadata: {
    textLength: number
    errorCount: number
    responseTime: number
    source?: string
  }): Promise<void> {
    const entry: TrackingEntry = {
      type: 'grammar-check',
      timestamp: Date.now(),
      metadata
    }
    
    
    // Add to local store immediately
    const usageEntry: UsageEntry = {
      timestamp: entry.timestamp,
      model: 'grammar-check',
      provider: 'local',
      tokenUsage: {
        prompt: 0,
        completion: 0,
        total: metadata.textLength,
        cost: 0
      },
      responseTime: metadata.responseTime,
      success: true
    }
    usageActions.recordUsage(usageEntry)
    // Usage entry recorded
    
    // Queue for server sync
    this.syncQueue.push(entry)
    
    // Try immediate sync if online
    if (this.isOnline) {
      this.syncToServer([entry]).catch(err => {
      })
    }
    
    // Trigger batch sync if queue is large
    if (this.syncQueue.length >= this.BATCH_SIZE) {
      this.syncPendingData()
    }
  }
  
  /**
   * Track AI analysis usage
   */
  async trackAIUsage(metadata: {
    model: string
    provider: string
    tokens: number
    cost: number
    responseTime: number
    success: boolean
    feature?: 'paraphrase' | 'expand' | 'shrink' | 'summarize' | 'continue' | 'critic' | 'generate' // AI feature type
  }): Promise<void> {
    const entry: TrackingEntry = {
      type: 'ai-analysis',
      timestamp: Date.now(),
      metadata
    }
    
    // Add to local store immediately
    const usageEntry: UsageEntry = {
      timestamp: entry.timestamp,
      model: metadata.model,
      provider: metadata.provider,
      tokenUsage: {
        prompt: 0,
        completion: 0,
        total: metadata.tokens,
        cost: metadata.cost
      },
      responseTime: metadata.responseTime,
      success: metadata.success
    }
    usageActions.recordUsage(usageEntry)
    
    // Queue for server sync
    this.syncQueue.push(entry)
    
    // Try immediate sync if online
    if (this.isOnline) {
      this.syncToServer([entry]).catch(err => {
      })
    }
    
    // Trigger batch sync if queue is large
    if (this.syncQueue.length >= this.BATCH_SIZE) {
      this.syncPendingData()
    }
  }
  
  /**
   * Sync pending data to server
   */
  async syncPendingData(force = false): Promise<SyncResult> {
    if (!this.isOnline && !force) {
      return { success: false, synced: 0, failed: 0, timestamp: Date.now() }
    }
    
    if (this.syncQueue.length === 0) {
      return { success: true, synced: 0, failed: 0, timestamp: Date.now() }
    }
    
    // Take a batch from the queue
    const batch = this.syncQueue.splice(0, this.BATCH_SIZE)
    
    try {
      const result = await this.syncToServer(batch)
      
      // Re-queue failed items for retry
      if (result.failed > 0) {
        const failedEntries = batch.slice(batch.length - result.failed)
        this.syncQueue.unshift(...failedEntries)
      }
      
      return result
    } catch (error) {
      // Re-queue all items on complete failure
      this.syncQueue.unshift(...batch)
      return { success: false, synced: 0, failed: batch.length, timestamp: Date.now() }
    }
  }
  
  /**
   * Sync data to server
   */
  private async syncToServer(entries: TrackingEntry[]): Promise<SyncResult> {
    try {
      const requestHeaders = await getSubscriptionRequestHeaders()
      
      const response = await fetch(`${this.PROXY_URL}/api/usage/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...requestHeaders
        },
        body: JSON.stringify({ entries })
      })
      
      if (!response.ok) {
        throw new Error(`Server sync failed: ${response.status}`)
      }
      
      const data = await response.json()
      return {
        success: true,
        synced: data.synced || 0,
        failed: data.failed || 0,
        timestamp: Date.now()
      }
    } catch (error) {
      throw error
    }
  }
  
  /**
   * Get usage summary from server
   */
  async getServerSummary(period: 'today' | 'month' = 'today'): Promise<any> {
    const cacheKey = `summary_${period}`;
    
    // Check if request is already pending
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      return pending;
    }
    
    // Check cache
    const cached = this.rateLimitCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.RATE_LIMIT_CACHE_TTL) {
      return cached.result;
    }
    
    // Create new request
    const request = this.fetchServerSummary(period, cacheKey);
    this.pendingRequests.set(cacheKey, request);
    
    try {
      const result = await request;
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }
  
  private async fetchServerSummary(period: 'today' | 'month', cacheKey: string): Promise<any> {
    try {
      const httpClient = HttpClient.getInstance();
      const requestHeaders = await getSubscriptionRequestHeaders();
      
      const data = await httpClient.request<any>(
        `${this.PROXY_URL}/api/usage/summary/${period}`,
        {
          method: 'GET',
          headers: requestHeaders,
          maxRetries: 3,
          retryDelay: 2000
        }
      );
      
      // Cache successful result
      this.rateLimitCache.set(cacheKey, { result: data, timestamp: Date.now() });
      return data;
    } catch (error) {
      console.error('Failed to get server summary:', error)
      // Fallback to local data
      const localData = usageActions.getUsageSummary(period === 'month' ? 'thisMonth' : 'today')
      // Cache even the fallback to prevent repeated failures
      this.rateLimitCache.set(cacheKey, { result: localData, timestamp: Date.now() });
      return localData;
    }
  }
  
  /**
   * Check rate limits with caching
   */
  async checkRateLimit(type: 'grammar' | 'ai' = 'grammar'): Promise<{
    allowed: boolean
    hourly?: { used: number; limit: number; remaining: number }
    daily?: { used: number; limit: number; remaining: number }
    minute?: { used: number; limit: number; remaining: number }
  }> {
    const cacheKey = `ratelimit:${type}`
    
    // Check cache first
    const cached = this.rateLimitCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.RATE_LIMIT_CACHE_TTL) {
      return cached.result
    }
    
    try {
      const requestHeaders = await getSubscriptionRequestHeaders()
      const response = await fetch(`${this.PROXY_URL}/api/usage/ratelimit/${type}`, {
        headers: requestHeaders
      })
      
      if (!response.ok) {
        console.warn(`Rate limit check failed with status ${response.status}, allowing by default`)
        // Only allow on network/server errors (5xx, network issues)
        // Don't allow on client errors (4xx) which might indicate actual limit exceeded
        const result = { allowed: response.status >= 500 }
        // Cache even error responses to prevent hammering the server
        this.rateLimitCache.set(cacheKey, { result, timestamp: Date.now() })
        return result
      }

      const data = await response.json()
      const result = data.rateLimit || { allowed: false } // Default to NOT allowed if no data

      // Cache the result
      this.rateLimitCache.set(cacheKey, { result, timestamp: Date.now() })
      
      return result
    } catch (error) {
      // Only allow on network failure (server unreachable)
      // This handles cases like offline, DNS failure, server down
      console.error('Failed to check rate limit:', error)
      const isNetworkError = error instanceof TypeError &&
                             (error.message.includes('Failed to fetch') ||
                              error.message.includes('NetworkError') ||
                              error.message.includes('ERR_NETWORK'))
      const result = { allowed: isNetworkError } // Only allow on actual network failure
      // Cache error responses to prevent hammering the server
      this.rateLimitCache.set(cacheKey, { result, timestamp: Date.now() })
      return result
    }
  }
  
  /**
   * Clear rate limit cache (useful after reset)
   */
  clearRateLimitCache() {
    this.rateLimitCache.clear()
  }
  
  /**
   * Force sync all pending data
   */
  async forceSyncAll(): Promise<SyncResult> {
    let totalSynced = 0
    let totalFailed = 0
    
    while (this.syncQueue.length > 0) {
      const result = await this.syncPendingData(true)
      totalSynced += result.synced
      totalFailed += result.failed
      
      // Break if we're not making progress
      if (result.failed === this.syncQueue.length) {
        break
      }
    }
    
    return {
      success: totalFailed === 0,
      synced: totalSynced,
      failed: totalFailed,
      timestamp: Date.now()
    }
  }
  
  /**
   * Get pending sync count
   */
  getPendingCount(): number {
    return this.syncQueue.length
  }
  
  /**
   * Reset usage limits and clear cached data
   */
  async resetUsageLimits(): Promise<boolean> {
    try {
      // Clear local cache server data
      const response = await fetch(`${this.PROXY_URL}/api/usage/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-dev-reset': 'true' // Add dev header for reset permission
        }
      })
      
      if (!response.ok) {
        console.warn('Failed to reset server usage data:', response.status)
        console.warn('Server may not support reset endpoint, continuing with local reset...')
      } else {
      }
      
      // Clear local sync queue
      this.syncQueue = []
      
      // Clear rate limit cache
      this.clearRateLimitCache()
      
      // Clear local usage store
      usageActions.resetAllData()
      
      // Clear localStorage entries related to usage
      if (typeof window !== 'undefined' && window.localStorage) {
        const keysToRemove = [
          'grammar-check-usage',
          'korean-grammar:usage',
          'grammar-check-trial-start',
          'usage-last-reset',
          'usage-tracking-cache'
        ]
        
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key)
          } catch (e) {
            console.warn(`Failed to remove ${key} from localStorage:`, e)
          }
        })
      }
      
      return true
    } catch (error) {
      console.error('Failed to reset usage limits:', error)
      return false
    }
  }
  
  /**
   * Cleanup and destroy
   */
  destroy() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer)
      this.syncTimer = null
    }
    
    // Final sync attempt
    this.forceSyncAll()
  }
}

// Export singleton instance
export const usageTracker = UsageTrackingService.getInstance()
